﻿import { useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import { DateTime } from "luxon";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "react-datepicker/dist/react-datepicker.css";

import { baseCrumbClasses } from "./Breadcrumbs";
import { monthOf, useShiftCalendar } from "../Hooks/useShiftCalendar";

interface DateSelectCrumbProps {
  selectedDate: DateTime;
  setSelectedDate: (date: DateTime) => void;
}

export const DateSelectCrumb = ({ selectedDate, setSelectedDate }: DateSelectCrumbProps) => {
  const [calendarVisible, setCalendarVisible] = useState(false);
  const [viewedMonth, setViewedMonth] = useState<Date>(
    () => selectedDate ? monthOf(selectedDate) : new Date()
  );

  const openCalendar = () => {
    if (selectedDate) {
      setViewedMonth(new Date(selectedDate.year, selectedDate.month - 1, selectedDate.day));
    }
    setCalendarVisible(true);
  };

  const toggleCalendar = () => {
    if (calendarVisible) setCalendarVisible(false);
    else openCalendar();
  };

  const { isDateAllowed, renderDayContents, isFetching } = useShiftCalendar(viewedMonth, calendarVisible);

  const onPick = (date: Date | null) => {
    if (!date) return;
    const sys = DateTime.fromJSDate(date, { zone: "system" });
    setSelectedDate(DateTime.utc(sys.year, sys.month, sys.day));
  };

  const selectedForPicker = useMemo(
    () => selectedDate ? monthOf(selectedDate) : null,
    [selectedDate]
  );

  return (
    <>
      <button
        className={`${baseCrumbClasses} text-sm compact:text-xs md:text-base p-2 compact:py-1 compact:px-4 lg:px-6`}
        onMouseEnter={openCalendar}
        onClick={toggleCalendar}
      >
        {selectedDate ? selectedDate.toISODate() : "Select a Date"}
      </button>

      {calendarVisible && (
        <div className={`absolute mt-2 left-0 top-8 z-20 transition-opacity ${isFetching ? "opacity-60" : ""}`}>
          <div
            className={`absolute z-30 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-2xl transition ${isFetching ? "opacity-100" : "opacity-0 pointer-events-none"
              }`}
          >
            <FontAwesomeIcon icon={faSpinner} spin />
          </div>

          <DatePicker
            disabled={isFetching}
            selected={selectedForPicker}
            onClickOutside={() => setCalendarVisible(false)}
            renderDayContents={renderDayContents}
            closeOnScroll
            onChange={onPick}
            inline
            filterDate={(d) => (isFetching ? true : isDateAllowed(d))}
            onMonthChange={setViewedMonth}
            onYearChange={setViewedMonth}
          />
        </div>
      )}
    </>
  );
};